import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../core/services/auth.service';
import { UserService } from '../core/services/user.service';
import { User } from '../shared/models/user.model';
import { RecipesStore } from './recipes.store';
import { ProductsStore } from './products.store';
import { IngredientsStore } from './ingredients.store';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@angular-architects/ngrx-toolkit';
import { SnackbarService } from '../core/services/snackbar.service';
import { UsersStore } from './users.store';

export interface AuthState {
  user: User | null;
  token: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
};

const collectionName = 'auth';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState({ collection: collectionName }),
  withComputed(({ user }) => ({
    userName: computed(() =>
      user() ? user().firstName + ' ' + user().lastName : undefined
    ),
  })),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      userService = inject(UserService),
      router = inject(Router),
      snackbarService = inject(SnackbarService),
      recipesStore = inject(RecipesStore),
      productsStore = inject(ProductsStore),
      ingredientsStore = inject(IngredientsStore),
      usersStore = inject(UsersStore)
    ) => ({
      login(credentials: { email: string; password: string }) {
        patchState(store, setLoading(collectionName));

        authService.login(credentials).subscribe({
          next: (response) => {
            const { token } = response;
            authService.setToken(token);

            this.initializeAuth();
            // Initialize counts after successful login
            recipesStore.initialize();
            productsStore.initialize();
            ingredientsStore.initialize();
            usersStore.initialize();
            router.navigate(['/dashboard']);
          },
          error: (error) => {
            const errorMessage = error.error?.message || 'Login failed';
            snackbarService.show(errorMessage, 'error');
            patchState(store, {
              ...setLoaded(collectionName),
              ...setError(errorMessage, collectionName),
            });
          },
        });
      },
      logout() {
        authService.logout();
        router.navigate(['/login']).then(() => {
          this.restore();
        });
      },
      initializeAuth() {
        const token = authService.getToken();
        if (token && authService.isTokenValid(token)) {
          try {
            const decodedToken = authService.decodeToken(token);
            if (decodedToken.id) {
              userService.loadById(decodedToken.id).subscribe({
                next: (fullUserData) => {
                  patchState(
                    store,
                    {
                      user: fullUserData,
                      token,
                    },
                    setLoaded(collectionName)
                  );
                },
                error: (error) => {
                  patchState(store, {
                    ...setLoaded(collectionName),
                    ...setError(error.error?.message, collectionName),
                  });
                  console.error('Failed to load user data on init:', error);
                  authService.logout();
                },
              });
            }
          } catch (error) {
            console.error('Error decoding token:', error);
            authService.logout();
          }
        } else if (token) {
          authService.logout();
        }
      },
      isLoggedIn(): boolean {
        return authService.isLoggedIn();
      },
      validateToken() {
        authService.validateTokenAndLogoutIfInvalid();
      },
      restore() {
        patchState(store, initialState);
      },
    })
  )
);
